import type { Order, OrderStatus } from '../types';

/**
 * Календарь заказов в духе itch.io: горизонтальная лента дней, каждый заказ —
 * полоса от начала работы до дедлайна. Раскладка живёт отдельно от компонента,
 * потому что вся её суть — арифметика дат, которую нужно проверять тестами.
 */
export type CalendarGroup = 'inProgress' | 'queued' | 'deferred';

export const CALENDAR_GROUP_LABELS: Record<CalendarGroup, string> = {
  inProgress: 'В работе',
  queued: 'В очереди',
  deferred: 'Отложены',
};

const GROUP_BY_STATUS: Record<OrderStatus, CalendarGroup> = {
  IN_PROGRESS: 'inProgress',
  REVIEW: 'inProgress',
  QUEUED: 'queued',
  WAITING_PAYMENT: 'deferred',
  WAITING_FINAL_PAYMENT: 'deferred',
  PAUSED: 'deferred',
  COMPLETED: 'deferred',
  CANCELED: 'deferred',
};

const GROUP_ORDER: CalendarGroup[] = ['inProgress', 'queued', 'deferred'];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Сколько дней показываем вокруг данных, чтобы лента не липла к краям. */
const PADDING_DAYS = 2;

/** Минимальная ширина ленты: на одном заказе календарь не должен схлопываться. */
const MIN_DAYS = 14;

export interface CalendarBar {
  order: Order;
  group: CalendarGroup;
  /** Смещение в днях от начала ленты. */
  offsetDays: number;
  /** Длительность в днях, минимум один. */
  lengthDays: number;
  /** Дедлайна нет: полоса условная, до конца ленты её тянуть нечестно. */
  openEnded: boolean;
}

export interface CalendarLayout {
  start: Date;
  days: Date[];
  bars: CalendarBar[];
  /** Смещение сегодняшнего дня или `null`, если он вне ленты. */
  todayOffset: number | null;
}

export function toUtcDay(value: string | Date): Date {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

export function calendarGroupOf(order: Order): CalendarGroup {
  return GROUP_BY_STATUS[order.status];
}

/**
 * Начало полосы — плановая дата начала, если художник её поставил: она и есть
 * его решение о том, когда взяться. Нет — берётся фактическое начало работы, а
 * если и его нет, то появление заказа. Конец — дедлайн; без дедлайна полоса
 * короткая и помечена как открытая, потому что растягивать её до края ленты
 * значило бы соврать о сроке.
 */
export function buildCalendarLayout(orders: Order[], today: Date = new Date()): CalendarLayout {
  const todayUtc = toUtcDay(today);

  if (orders.length === 0) {
    const start = addDays(todayUtc, -PADDING_DAYS);
    return {
      start,
      days: rangeOfDays(start, MIN_DAYS),
      bars: [],
      todayOffset: PADDING_DAYS,
    };
  }

  const spans = orders.map((order) => {
    const from = toUtcDay(order.plannedStartAt ?? order.startedAt ?? order.createdAt);
    const deadline = order.deadline ? toUtcDay(order.deadline) : null;

    return {
      order,
      from,
      // Полоса без дедлайна показывается коротким отрезком в три дня.
      to: deadline && deadline >= from ? deadline : addDays(from, deadline ? 0 : 2),
      openEnded: deadline === null,
    };
  });

  const earliest = spans.reduce((min, span) => (span.from < min ? span.from : min), spans[0]!.from);
  const latest = spans.reduce((max, span) => (span.to > max ? span.to : max), spans[0]!.to);
  const start = addDays(min(earliest, todayUtc), -PADDING_DAYS);
  const end = addDays(max(latest, todayUtc), PADDING_DAYS);
  const days = rangeOfDays(start, Math.max(MIN_DAYS, daysBetween(start, end) + 1));

  const bars = spans
    .map((span) => ({
      order: span.order,
      group: calendarGroupOf(span.order),
      offsetDays: daysBetween(start, span.from),
      lengthDays: Math.max(1, daysBetween(span.from, span.to) + 1),
      openEnded: span.openEnded,
    }))
    .sort(compareBars);

  const todayOffset = daysBetween(start, todayUtc);

  return {
    start,
    days,
    bars,
    todayOffset: todayOffset >= 0 && todayOffset < days.length ? todayOffset : null,
  };
}

/**
 * Сверху то, что делается прямо сейчас, ниже очередь, в самом низу отложенное.
 * Внутри группы порядок — по очереди и сроку, чтобы соседние строки читались
 * как план работы.
 */
function compareBars(left: CalendarBar, right: CalendarBar): number {
  const byGroup = GROUP_ORDER.indexOf(left.group) - GROUP_ORDER.indexOf(right.group);
  if (byGroup !== 0) {
    return byGroup;
  }

  const leftQueue = left.order.queuePosition ?? Number.MAX_SAFE_INTEGER;
  const rightQueue = right.order.queuePosition ?? Number.MAX_SAFE_INTEGER;
  if (leftQueue !== rightQueue) {
    return leftQueue - rightQueue;
  }

  const byDeadline = (left.order.deadline ?? '9999').localeCompare(right.order.deadline ?? '9999');
  return byDeadline !== 0 ? byDeadline : left.offsetDays - right.offsetDays;
}

function rangeOfDays(start: Date, length: number): Date[] {
  return Array.from({ length }, (_, index) => addDays(start, index));
}

function min(left: Date, right: Date): Date {
  return left < right ? left : right;
}

function max(left: Date, right: Date): Date {
  return left > right ? left : right;
}
